import {
  CSSProperties,
  memo,
  ReactElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef
} from 'react';
import cc from 'classcat';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { Container, CustomData, CustomMask } from './style';
import { Portal } from 'components/Shared/Portal';

type Props = {
  open: boolean;
  className?: string;
  children: any;
  onMaskClick?: (arg: boolean) => void;
  mask?: boolean;
  maskBackground?: string;
  maskPosTop?: {
    usePosTop?: boolean;
    posTop?: string;
  };
  animationType?: 'toRight' | 'toLeft' | 'toTop' | 'toBottom' | 'none';
  animationTime?: string;
  posTop?: string;
  posBottom?: string;
  posLeft?: string;
  posRight?: string;
  style?: CSSProperties;
  onBeforeOpen?: () => void;
  onAnimationEnd?: (value?: boolean) => void;
  initiallyLoaded?: boolean;
};

export const Popup = memo(
  ({
    open,
    children,
    onMaskClick,
    mask,
    animationType,
    animationTime,
    className,
    posBottom,
    posLeft,
    posRight,
    posTop,
    maskPosTop,
    maskBackground,
    style = {},
    onBeforeOpen,
    onAnimationEnd,
    initiallyLoaded = false
  }: Props): ReactElement | null => {
    const loaded = useRef(initiallyLoaded);

    const animationTypeFromParams = useMemo(() => {
      if (animationType) {
        return animationType;
      }

      return 'toLeft';
    }, [animationType]);

    useLayoutEffect(() => {
      onBeforeOpen?.();
    }, []);

    useEffect(() => {
      if (open) {
        loaded.current = true;
      }
    }, [open]);

    if (SpringConfigs.MOCKED_DATA) {
      return null;
    }

    return (
      <Portal>
        <Container
          show={open}
          animationTime={animationTime}
          className={className}
          style={style}
          $maskPosTop={maskPosTop?.usePosTop ? posTop : maskPosTop?.posTop}
        >
          {mask && (
            <CustomMask
              className="customMask"
              maskBackground={maskBackground}
              onClick={() => onMaskClick?.(true)}
            />
          )}
          <CustomData
            className={cc([
              'custom-data',
              {
                'customPopup--open': open,
                'customPopup--close': !open
              }
            ])}
            animationType={animationTypeFromParams}
            animationTime={animationTime}
            posTop={posTop}
            posRight={posRight}
            posLeft={posLeft}
            posBottom={posBottom}
            onAnimationEnd={() => {
              onAnimationEnd?.(open);
            }}
          >
            {(open || loaded.current) && children}
          </CustomData>
        </Container>
      </Portal>
    );
  }
);
